import { ExcludedMeetingType } from "#webapp/src/__schema__";
import useMessageAndSeverity from "#webapp/src/components/hooks/useMessageAndSeverity";
import getNextOccuranceText from "#webapp/src/util/getNextOccuranceText";
import { useMutation } from "@apollo/client";
import { logger } from "@clockwise/client-commons/src/util/logger";
import {
  EventTable,
  EventTableHeading,
  EventTableRow,
} from "@clockwise/web-commons/src/components/EventTable/";
import { TrackingEvents, useTracking } from "@clockwise/web-commons/src/util/analytics.util";
import React, { useEffect, useMemo, useState } from "react";
import { throttle } from "throttle-debounce";
import { SetCalendarEventFlexibilityDocument } from "../../graphql/__generated__/SetCalendarEventFlexibility.generated";
import useFlexibleMeetings from "../hooks/useFlexibleMeetings";
import { useOrgId } from "../hooks/useOrgId";
import { FlexibleMeeting } from "./types";

type Props = {
  excludeOneOnOnes?: boolean;
};

const trackingSourceId = "My flexible meetings control";

const MyFlexMeetings = ({ excludeOneOnOnes = false }: Props) => {
  const [meetings, setMeetings] = useState<FlexibleMeeting[]>([]);
  const { setInfo, setError } = useMessageAndSeverity();
  const trackEvent = useTracking();

  const { orgId } = useOrgId();

  const excludeList = excludeOneOnOnes ? [ExcludedMeetingType.OneOnOne] : [];
  const { flexibleMeetings, loading, error } = useFlexibleMeetings(excludeList);

  const [setFlexibility] = useMutation(SetCalendarEventFlexibilityDocument);

  const setFlexibilityThrottled = useMemo(() => throttle(250, setFlexibility), [setFlexibility]);

  useEffect(() => {
    if (flexibleMeetings) {
      setMeetings(flexibleMeetings);
    }
  }, [flexibleMeetings]);

  const updateMeetingFlexibility = (id: string, flexible: boolean) => {
    setMeetings((state) => {
      return state.map((meeting) => {
        if (meeting.id === id) {
          return Object.assign({}, meeting, { flexible });
        }

        return meeting;
      });
    });
  };

  const handleFlexibilityChangeSuccess = (eventId: string, isFlexible: boolean) => {
    setInfo("Successfully updated flexibility");
    trackEvent(TrackingEvents.FLEXIBLE_MEETING.UPDATE_FLEXIBILITY, {
      isFlexible,
      eventId,
      source: trackingSourceId,
    });
  };
  const handleFlexibilityChangeError = () => {
    setError("Unable to update flexibility");
    logger.error("Failed to update flexibility");
  };

  const handleFlexibilityChange = (eventId: string, isFlexible: boolean) => {
    const eventsToEnable = [];
    const eventsToDisable = [];

    // error if missing required prop
    if (!orgId) {
      handleFlexibilityChangeError();
      return;
    }

    if (isFlexible) {
      eventsToEnable.push(eventId);
    } else {
      eventsToDisable.push(eventId);
    }

    // optimistically update UI
    updateMeetingFlexibility(eventId, isFlexible);

    void setFlexibilityThrottled({
      variables: { input: { orgId, eventsToEnable, eventsToDisable } },
      onCompleted: () => handleFlexibilityChangeSuccess(eventId, isFlexible),
      onError: () => {
        handleFlexibilityChangeError();
        updateMeetingFlexibility(eventId, !isFlexible);
      },
    });
  };

  return (
    <>
      <div className="cw-mb-3">
        <EventTableHeading headingLevel="h2" heading="Other flexible meetings" />
      </div>
      <div>
        <EventTable
          emptyText="No other flexible meetings to display at this time"
          errorText={
            error
              ? "There was an error fetching your flexible meetings. Please try again."
              : undefined
          }
          loading={loading}
          onFlexibilityChange={handleFlexibilityChange}
          showAvatars
        >
          {meetings.map(({ attendees, id, title, startTime, recurrenceText, flexible }) => (
            <EventTableRow
              attendees={attendees}
              eventId={id}
              flexible={flexible}
              key={id}
              nextOccruance={startTime ? getNextOccuranceText(startTime) : "Unknown Date"}
              recurrence={recurrenceText}
              title={title}
            />
          ))}
        </EventTable>
      </div>
    </>
  );
};

export default MyFlexMeetings;
